/**
 * Operators Management View
 * CRUD operations for operators
 */

import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu,
  Search,
  Plus,
  Trash2,
  Edit,
  Copy,
  Play,
  ChevronDown,
  ChevronRight,
  Clock,
  TrendingUp,
  CheckCircle,
  MoreVertical,
  Loader2,
  X,
  RefreshCw,
  Zap,
  ExternalLink,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { useDashboardStore } from "@/store";
import { convexService } from "@/services/convexService";

type OperatorStatus = "active" | "inactive" | "error" | "testing";

interface DisplayOperator {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  method: string;
  authType: string;
  timeout: number;
  successRate: number;
  avgLatency: number;
  version: string;
  status: OperatorStatus;
  lastInvoked: Date;
  createdAt: Date;
}

export function OperatorsView() {
  const { settings, addLogEntry } = useDashboardStore();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OperatorStatus | "all">("all");
  const [sortBy, setSortBy] = useState<"name" | "successRate" | "latency" | "recent">("name");
  const [expandedOperator, setExpandedOperator] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<DisplayOperator | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    endpoint: "",
    method: "POST",
    authType: "none",
    timeout: 30000,
  });

  // Mock data - replace with Convex calls
  const [operators, setOperators] = useState<DisplayOperator[]>([
    {
      id: "1",
      name: "Weather API",
      description: "Fetches current weather data for specified locations",
      endpoint: "https://api.weatherapi.com/v1/current.json",
      method: "GET",
      authType: "apiKey",
      timeout: 10000,
      successRate: 0.98,
      avgLatency: 245,
      version: "1.0.0",
      status: "active",
      lastInvoked: new Date("2024-12-28T10:30:00"),
      createdAt: new Date("2024-12-15"),
    },
    {
      id: "2",
      name: "Code Generator",
      description: "Generates code using Qwen-Coder-Plus model",
      endpoint: "https://apis.iflow.cn/v1/chat/completions",
      method: "POST",
      authType: "bearer",
      timeout: 60000,
      successRate: 0.94,
      avgLatency: 1850,
      version: "2.1.0",
      status: "active",
      lastInvoked: new Date("2024-12-28T09:45:00"),
      createdAt: new Date("2024-12-10"),
    },
    {
      id: "3",
      name: "Deep Reasoning",
      description: "Performs deep logical reasoning using DeepSeek-R1",
      endpoint: "https://apis.iflow.cn/v1/chat/completions",
      method: "POST",
      authType: "bearer",
      timeout: 120000,
      successRate: 0.91,
      avgLatency: 3240,
      version: "1.5.0",
      status: "active",
      lastInvoked: new Date("2024-12-27T14:20:00"),
      createdAt: new Date("2024-12-18"),
    },
    {
      id: "4",
      name: "GLM Orchestrator",
      description: "General task orchestration using GLM-4.7",
      endpoint: "https://api.z.ai/api/coding/paas/v4/chat/completions",
      method: "POST",
      authType: "bearer",
      timeout: 90000,
      successRate: 0.96,
      avgLatency: 1205,
      version: "4.7.0",
      status: "active",
      lastInvoked: new Date("2024-12-28T11:00:00"),
      createdAt: new Date("2024-12-05"),
    },
  ]);

  const handleCreateOperator = async () => {
    if (!formValues.name || !formValues.endpoint) return;

    setIsSaving(true);
    setError(null);

    try {
      try {
        await convexService.mutate("operators:create", {
          name: formValues.name,
          description: formValues.description,
          endpoint: formValues.endpoint,
          method: formValues.method,
          authType: formValues.authType,
          timeout: formValues.timeout,
        });
      } catch {
        // Convex not available - continue with local
      }

      const newId = String(operators.length + 1);
      setOperators([
        ...operators,
        {
          id: newId,
          name: formValues.name,
          description: formValues.description,
          endpoint: formValues.endpoint,
          method: formValues.method,
          authType: formValues.authType,
          timeout: formValues.timeout,
          successRate: 1.0,
          avgLatency: 0,
          version: "1.0.0",
          status: "active",
          lastInvoked: new Date(),
          createdAt: new Date(),
        },
      ]);

      addLogEntry({
        category: "operator",
        action: "Operator Created",
        details: `Created operator "${formValues.name}"`,
        severity: "info",
        source: "operators-view",
      });

      resetForm();
      setIsAddDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create operator");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateOperator = async () => {
    if (!selectedOperator) return;

    setIsSaving(true);
    setError(null);

    try {
      try {
        await convexService.mutate("operators:update", {
          id: selectedOperator.id,
          name: formValues.name,
          description: formValues.description,
          endpoint: formValues.endpoint,
          method: formValues.method,
          authType: formValues.authType,
          timeout: formValues.timeout,
        });
      } catch {
        // Convex not available - continue with local
      }

      setOperators(
        operators.map((op) =>
          op.id === selectedOperator.id
            ? {
                ...op,
                name: formValues.name,
                description: formValues.description,
                endpoint: formValues.endpoint,
                method: formValues.method,
                authType: formValues.authType,
                timeout: formValues.timeout,
              }
            : op
        )
      );

      addLogEntry({
        category: "operator",
        action: "Operator Updated",
        details: `Updated operator "${formValues.name}"`,
        severity: "info",
        source: "operators-view",
      });

      resetForm();
      setIsEditDialogOpen(false);
      setSelectedOperator(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update operator");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteOperator = async (operatorId: string) => {
    if (!confirm("Are you sure you want to delete this operator?")) return;

    try {
      try {
        await convexService.mutate("operators:remove", { id: operatorId });
      } catch {
        // Convex not available - continue with local
      }

      setOperators(operators.filter((op) => op.id !== operatorId));

      addLogEntry({
        category: "operator",
        action: "Operator Deleted",
        details: `Deleted operator ${operatorId}`,
        severity: "warning",
        source: "operators-view",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete operator");
    }
  };

  const handleTestOperator = async (operator: DisplayOperator) => {
    setOperators(
      operators.map((op) =>
        op.id === operator.id ? { ...op, status: "testing" as const } : op
      )
    );

    addLogEntry({
      category: "operator",
      action: "Testing Operator",
      details: `Testing operator "${operator.name}" at ${operator.endpoint}`,
      severity: "info",
      source: "operators-view",
    });

    // Simulate test
    setTimeout(() => {
      const success = Math.random() > 0.2;
      setOperators(
        operators.map((op) =>
          op.id === operator.id
            ? {
                ...op,
                status: success ? "active" : "error",
                successRate: success ? op.successRate : Math.max(0, op.successRate - 0.1),
                lastInvoked: new Date(),
              }
            : op
        )
      );

      addLogEntry({
        category: "operator",
        action: success ? "Operator Test Passed" : "Operator Test Failed",
        details: success
          ? `Operator "${operator.name}" is responding correctly`
          : `Operator "${operator.name}" is not responding`,
        severity: success ? "info" : "error",
        source: "operators-view",
      });
    }, 2000);
  };

  const resetForm = () => {
    setFormValues({
      name: "",
      description: "",
      endpoint: "",
      method: "POST",
      authType: "none",
      timeout: 30000,
    });
  };

  const openEditDialog = (operator: DisplayOperator) => {
    setSelectedOperator(operator);
    setFormValues({
      name: operator.name,
      description: operator.description,
      endpoint: operator.endpoint,
      method: operator.method,
      authType: operator.authType,
      timeout: operator.timeout,
    });
    setIsEditDialogOpen(true);
  };

  const filteredOperators = useMemo(() => {
    let result = [...operators];

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (op) =>
          op.name.toLowerCase().includes(searchLower) ||
          op.description.toLowerCase().includes(searchLower) ||
          op.endpoint.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((op) => op.status === statusFilter);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "successRate":
          return b.successRate - a.successRate;
        case "latency":
          return a.avgLatency - b.avgLatency;
        case "recent":
          return b.lastInvoked.getTime() - a.lastInvoked.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [operators, search, statusFilter, sortBy]);

  const stats = useMemo(
    () => ({
      total: operators.length,
      active: operators.filter((op) => op.status === "active").length,
      avgSuccessRate:
        operators.length > 0
          ? operators.reduce((acc, op) => acc + op.successRate, 0) / operators.length
          : 0,
      avgLatency:
        operators.length > 0
          ? operators.reduce((acc, op) => acc + op.avgLatency, 0) / operators.length
          : 0,
    }),
    [operators]
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-500/20 p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Operators" value={stats.total} icon={Cpu} />
        <StatCard
          label="Active"
          value={stats.active}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="Avg Success Rate"
          value={`${(stats.avgSuccessRate * 100).toFixed(0)}%`}
          icon={TrendingUp}
          color="purple"
        />
        <StatCard
          label="Avg Latency"
          value={`${Math.round(stats.avgLatency)}ms`}
          icon={Clock}
          color="yellow"
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search operators..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-2 pl-10 pr-4 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OperatorStatus | "all")}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="error">Error</option>
            <option value="testing">Testing</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm cursor-pointer"
          >
            <option value="name">Name</option>
            <option value="successRate">Success Rate</option>
            <option value="latency">Latency</option>
            <option value="recent">Recently Used</option>
          </select>

          <button
            disabled={isLoading}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-2 cursor-pointer hover:bg-[hsl(var(--muted))]"
            title="Refresh operators"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </button>

          <button
            onClick={() => {
              resetForm();
              setIsAddDialogOpen(true);
            }}
            className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-black cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            New Operator
          </button>
        </div>
      </div>

      {/* Operators List */}
      <div className="space-y-2">
        <AnimatePresence>
          {filteredOperators.map((operator) => (
            <OperatorCard
              key={operator.id}
              operator={operator}
              isExpanded={expandedOperator === operator.id}
              onToggle={() =>
                setExpandedOperator(expandedOperator === operator.id ? null : operator.id)
              }
              onAction={(action) => {
                if (action === "delete") {
                  handleDeleteOperator(operator.id);
                } else if (action === "edit") {
                  openEditDialog(operator);
                } else if (action === "test") {
                  handleTestOperator(operator);
                } else if (action === "duplicate") {
                  addLogEntry({
                    category: "operator",
                    action: "Operator Duplicated",
                    details: `Duplicated operator "${operator.name}"`,
                    severity: "info",
                    source: "operators-view",
                  });
                }
              }}
            />
          ))}
        </AnimatePresence>

        {filteredOperators.length === 0 && (
          <div className="flex h-40 items-center justify-center text-[hsl(var(--muted-foreground))]">
            <div className="text-center">
              <Search className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No operators found</p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Operator Dialog */}
      <OperatorDialog
        isOpen={isAddDialogOpen || isEditDialogOpen}
        mode={isEditDialogOpen ? "edit" : "add"}
        operator={isEditDialogOpen ? selectedOperator : undefined}
        formValues={formValues}
        setFormValues={setFormValues}
        isSaving={isSaving}
        onSave={isEditDialogOpen ? handleUpdateOperator : handleCreateOperator}
        onClose={() => {
          setIsAddDialogOpen(false);
          setIsEditDialogOpen(false);
          resetForm();
          setSelectedOperator(null);
        }}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color = "default",
}: {
  label: string;
  value: string | number;
  icon: typeof Cpu;
  color?: "default" | "green" | "yellow" | "purple" | "red";
}) {
  const colorClasses = {
    default: "text-[hsl(var(--foreground))]",
    green: "text-green-400",
    yellow: "text-yellow-400",
    purple: "text-purple-400",
    red: "text-red-400",
  };

  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">{label}</span>
        <Icon className={cn("h-4 w-4", colorClasses[color])} />
      </div>
      <p className={cn("mt-2 text-2xl font-bold", colorClasses[color])}>{value}</p>
    </div>
  );
}

function OperatorCard({
  operator,
  isExpanded,
  onToggle,
  onAction,
}: {
  operator: DisplayOperator;
  isExpanded: boolean;
  onToggle: () => void;
  onAction: (action: string) => void;
}) {
  const statusColors = {
    active: "bg-green-500/20 text-green-400",
    inactive: "bg-gray-500/20 text-gray-400",
    error: "bg-red-500/20 text-red-400",
    testing: "bg-yellow-500/20 text-yellow-400 animate-pulse",
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden"
    >
      <div className="flex cursor-pointer items-center gap-4 p-4" onClick={onToggle}>
        <button className="text-[hsl(var(--muted-foreground))] cursor-pointer">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--primary)/.2)]">
          {operator.status === "testing" ? (
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
          ) : (
            <Cpu className="h-5 w-5 text-[hsl(var(--primary))]" />
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{operator.name}</h3>
            <span className={cn("rounded-full px-2 py-0.5 text-xs", statusColors[operator.status])}>
              {operator.status}
            </span>
            <span className="rounded border border-[hsl(var(--border))] px-2 py-0.5 text-xs text-[hsl(var(--muted-foreground))]">
              {operator.method}
            </span>
          </div>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] truncate max-w-md">
            {operator.description}
          </p>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="text-[hsl(var(--muted-foreground))] text-xs">Success</p>
            <p className={operator.successRate > 0.9 ? "text-green-400" : "text-yellow-400"}>
              {(operator.successRate * 100).toFixed(0)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-[hsl(var(--muted-foreground))] text-xs">Latency</p>
            <p className={operator.avgLatency < 1000 ? "text-green-400" : "text-yellow-400"}>
              {Math.round(operator.avgLatency)}ms
            </p>
          </div>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button onClick={(e) => e.stopPropagation()} className="rounded p-1 hover:bg-[hsl(var(--muted))] cursor-pointer">
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="min-w-[160px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1 shadow-lg" sideOffset={5}>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("test")}>
                <Play className="h-4 w-4" /> Test
              </DropdownMenu.Item>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("edit")}>
                <Edit className="h-4 w-4" /> Edit
              </DropdownMenu.Item>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm hover:bg-[hsl(var(--muted))]" onClick={() => onAction("duplicate")}>
                <Copy className="h-4 w-4" /> Duplicate
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--border))]" />
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-red-400 hover:bg-red-500/20" onClick={() => onAction("delete")}>
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]"
          >
            <div className="p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Endpoint</h4>
                <div className="flex items-center gap-2">
                  <code className="rounded bg-[hsl(var(--background))] px-3 py-1.5 text-xs flex-1 font-mono break-all">
                    {operator.endpoint}
                  </code>
                  <a
                    href={operator.endpoint}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md p-1.5 bg-[hsl(var(--background))] hover:bg-[hsl(var(--muted))] cursor-pointer"
                    title="Open endpoint"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Auth Type</p>
                  <p className="font-mono">{operator.authType}</p>
                </div>
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Timeout</p>
                  <p className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {(operator.timeout / 1000).toFixed(1)}s
                  </p>
                </div>
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Version</p>
                  <p className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    {operator.version}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Created</p>
                  <p>{operator.createdAt.toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-[hsl(var(--muted-foreground))]">Last Invoked</p>
                  <p>{operator.lastInvoked.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function OperatorDialog({
  isOpen,
  mode,
  operator,
  formValues,
  setFormValues,
  isSaving,
  onSave,
  onClose,
}: {
  isOpen: boolean;
  mode: "add" | "edit";
  operator?: DisplayOperator | null;
  formValues: {
    name: string;
    description: string;
    endpoint: string;
    method: string;
    authType: string;
    timeout: number;
  };
  setFormValues: (values: any) => void;
  isSaving: boolean;
  onSave: () => void;
  onClose: () => void;
}) {

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold">
              {mode === "add" ? "Add New Operator" : "Edit Operator"}
            </Dialog.Title>
            <button onClick={onClose} className="rounded p-1 hover:bg-[hsl(var(--muted))] cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={formValues.name}
                onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                placeholder="e.g., Weather API"
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formValues.description}
                onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
                placeholder="Describe what this operator does..."
                rows={2}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Endpoint</label>
              <input
                type="url"
                value={formValues.endpoint}
                onChange={(e) => setFormValues({ ...formValues, endpoint: e.target.value })}
                placeholder="https://api.example.com/v1/endpoint"
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm font-mono focus:border-[hsl(var(--primary))] focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Method</label>
                <select
                  value={formValues.method}
                  onChange={(e) => setFormValues({ ...formValues, method: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm cursor-pointer"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Auth Type</label>
                <select
                  value={formValues.authType}
                  onChange={(e) => setFormValues({ ...formValues, authType: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm cursor-pointer"
                >
                  <option value="none">None</option>
                  <option value="apiKey">API Key</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Timeout (ms)</label>
              <input
                type="number"
                value={formValues.timeout}
                onChange={(e) => setFormValues({ ...formValues, timeout: parseInt(e.target.value) || 30000 })}
                min="1000"
                step="1000"
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:border-[hsl(var(--primary))] focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={onClose} className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm cursor-pointer hover:bg-[hsl(var(--muted))]">
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isSaving || !formValues.name || !formValues.endpoint}
              className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-black cursor-pointer disabled:opacity-50"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "add" ? "Add Operator" : "Update Operator"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
